import React, { useCallback, useRef, useState, useEffect } from 'react';
import fetchWithCsrf from 'js/utils/fetch_with_csrf';
import routes from 'js/core_pre_webpack/routes';
import {
  trackClick,
  Options as ClickTrackingOptions,
} from 'js/utils/trackClick';
import { emailRegExp } from 'js/utils/validation';

import { Input } from 'js/design_system/forms/Input';
import { Button } from 'js/design_system/Button';

import { TitleText, BodyText } from './common';
import { ModalPage, EmailEntryPageConfigurableText } from '../types';
import { BackButton } from '../components/BackButton';
import { NewLine } from '../components/NewLine';
import { CenteredDiv } from '../components/CenteredDiv';
import { VerticalSpacer } from '../components/VerticalSpacer';

type HasAccountResponse = {
  has_account?: boolean;
  email?: string;
  first_name?: string;
  message?: string;
  success?: boolean;
};

type Props = {
  setPage: React.Dispatch<React.SetStateAction<ModalPage>>;
  setUserEmail: React.Dispatch<React.SetStateAction<string>>;
  setUserFirstName: React.Dispatch<React.SetStateAction<string>>;
  show: boolean;
  clickTrackingContext?: Partial<ClickTrackingOptions>;
  text: EmailEntryPageConfigurableText;
};
export const EmailEntryPage: React.FC<Props> = (props) => {
  const {
    setPage,
    setUserEmail,
    setUserFirstName,
    show,
    clickTrackingContext,
    text,
  } = props;
  const emailInputRef = useRef<HTMLInputElement>(null);
  const [emailErrorText, setEmailErrorText] = useState('');

  useEffect(() => {
    if (show && emailInputRef.current) {
      emailInputRef.current.focus();
    }
  }, [show]);

  const handleSubmit = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.preventDefault();
      trackClick({
        source: 'login_modal_email_entry_submit_button',
        ...clickTrackingContext,
      });
      const email = emailInputRef.current?.value.toLowerCase() ?? '';
      if (emailRegExp.test(email)) {
        setEmailErrorText('');
      } else {
        setEmailErrorText('Please enter a valid email address.');
        return;
      }
      const resp = await fetchWithCsrf(
        routes.api_v0_has_account_path({ subdomain_param: 'api' }),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: email }),
        },
      );
      const respBody: HasAccountResponse = await resp.json();
      if (!resp.ok) {
        respBody.message
          ? setEmailErrorText(respBody.message)
          : setEmailErrorText('An error occurred. Please try again later.');
        return;
      }
      setUserEmail(respBody.email ? respBody.email : email);
      // TODO (ira): this should be renamed and we should error if success!==true
      if (respBody.has_account) {
        setUserFirstName(respBody.first_name ?? '');
        setPage('email-login');
      } else {
        setPage('email-registration');
      }
    },
    [clickTrackingContext, setPage, setUserEmail, setUserFirstName],
  );
  return (
    <div style={{ display: show ? 'initial' : 'none' }}>
      <BackButton
        onClick={() => {
          trackClick({
            source: 'login_modal_email_entry_back_button',
            ...clickTrackingContext,
          });
          setPage('buttons');
        }}
      />
      <TitleText>{text.header}</TitleText>
      <BodyText>{text.subheader}</BodyText>
      <VerticalSpacer height={40} />
      <Input
        label="Email"
        name="email"
        ref={emailInputRef}
        type="email"
        autoComplete="username"
        placeholder="name@example.com"
        required={show}
        hasError={!!emailErrorText}
        errors={emailErrorText ? [emailErrorText] : []}
      />
      <NewLine />
      <VerticalSpacer height={40} />
      <CenteredDiv>
        <Button
          size="lg"
          type="submit"
          disabled={!show}
          iconRight="arrow_forward"
          onClick={handleSubmit}
        >
          Next
        </Button>
      </CenteredDiv>
    </div>
  );
};
